import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { ChangeEvent, FocusEvent, SetStateAction, useContext, useEffect, useState } from 'react';

import 'react-phone-number-input/style.css';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { AddressProps } from '@/models/Address';
import { ContactConfig, ContactProps, ContactSubType, ContactType } from '@/models/Contact';
import { CountryProps } from '@/models/Country';
import { DepartmentTypeProps } from '@/models/DepartmentType';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';
import { getContactHasError } from '@/utils/contactValidationUtil';

import Address from '@/components/common/address_components/Address';
import CharacterLimitHelperText from '@/components/common/CharacterLimitHelperText';
import PhoneNumberSelect from '@/components/common/contact_components/PhoneNumberSelect';
import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { SelectInput } from '@/components/common/select_input/SelectInput';
import { Spinner } from '@/components/common/Spinner';
import { Email } from '@/components/common/text_input/Email';

const Department = ({
  setContactInfo,
  contactInfo,
  label,
  formSubmitted,
  contactTypeId,
}: {
  setContactInfo: React.Dispatch<SetStateAction<ContactProps>>;
  contactInfo: ContactProps;
  label: string;
  formSubmitted: boolean;
  contactTypeId: number;
}) => {
  const { contactTypes, departmentTypes } = useContext(ConfigContext);

  const contactType = contactTypes.find((c: ContactType) => c.id === contactTypeId);
  let departmentTypeOptions = contactType?.departmentTypes ?? [];

  if (
    departmentTypes != null &&
    departmentTypeOptions.length > 0 &&
    contactInfo?.departmentTypeId != null
  ) {
    const selectedDept = departmentTypes.find(
      (d: DepartmentTypeProps) => d.id === contactInfo.departmentTypeId
    );
    if (
      selectedDept != null &&
      !departmentTypeOptions.some((department) => department.id === selectedDept.id)
    ) {
      departmentTypeOptions = [...departmentTypeOptions, selectedDept];
    }
  }

  const showOtherDept = contactInfo?.departmentTypeId === 9 || contactInfo?.departmentTypeId === 10;

  return (
    <Grid container display={'flex'} flexDirection='column' size={12}>
      <FormControl
        variant='outlined'
        error={formSubmitted && contactInfo?.departmentTypeId == null}
        required
      >
        <InputLabel id={`department-select-label`}>{label}</InputLabel>

        <Select
          name={'departmentTypeId'}
          labelId='department-select-label'
          label={label}
          id='department-select'
          value={
            contactInfo?.departmentTypeId != null ? contactInfo.departmentTypeId.toString() : ''
          }
          required
          onChange={(e: SelectChangeEvent) => {
            const value = Number(e.target.value);
            setContactInfo((prevState: ContactProps) => ({
              ...prevState,
              departmentTypeId: value,
              ...(value !== 9 && value !== 10 ? { departmentOther: null } : {}),
            }));
          }}
        >
          {departmentTypeOptions?.map((department: DepartmentTypeProps) => {
            return (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {contactInfo?.departmentTypeId !== null && showOtherDept && (
        <>
          <TextField
            onBlur={(e) => {
              setContactInfo({
                ...contactInfo,
                departmentOther: e.target.value,
              });
            }}
            sx={{ flexGrow: 1, marginTop: '12px' }}
            defaultValue={contactInfo?.departmentOther != null ? contactInfo?.departmentOther : ''}
            label='Other Department'
            name={'departmentOther'}
            variant='outlined'
            inputProps={{ maxLength: 100 }}
          />
          {contactInfo.departmentOther != null && contactInfo.departmentOther.length >= 100 && (
            <CharacterLimitHelperText limit={100} />
          )}
        </>
      )}
    </Grid>
  );
};

const Certification = ({
  contactInfo,
  handleContactChange,
  formSubmitted,
}: {
  contactInfo: ContactProps;
  handleContactChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  formSubmitted: boolean;
}) => {
  return (
    <Grid container size={12}>
      <TextField
        fullWidth
        id='cert'
        defaultValue={contactInfo.certification}
        name={'certification'}
        label='Certification'
        variant='outlined'
        onChange={handleContactChange}
        error={formSubmitted && contactInfo.certification === ''}
        inputProps={{ maxLength: 255 }}
      />
      {contactInfo.certification != null && contactInfo.certification.length >= 255 && (
        <CharacterLimitHelperText limit={255} />
      )}
    </Grid>
  );
};

const Title = ({
  contactInfo,
  handleContactChange,
  selectOnFocus,
  titleLabel,
  formSubmitted,
}: {
  contactInfo: ContactProps;
  handleContactChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectOnFocus: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  titleLabel: string;
  formSubmitted: boolean;
}) => {
  return (
    <Grid container size={12}>
      <TextField
        fullWidth
        id='title'
        defaultValue={contactInfo.title}
        name={'title'}
        variant='outlined'
        required
        label={titleLabel}
        onFocus={selectOnFocus}
        onChange={handleContactChange}
        error={formSubmitted && (contactInfo.title === '' || contactInfo.title === null)}
        inputProps={{ maxLength: 50 }}
      />
      {contactInfo.title != null && contactInfo.title.length >= 50 && (
        <CharacterLimitHelperText limit={50} />
      )}
    </Grid>
  );
};

const TitleDropdown = ({
  contactInfo,
  contactSubTypes,

  setContactInfo,
  titleLabel,
  formSubmitted,
}: {
  contactInfo: ContactProps;
  contactSubTypes: ContactSubType[];
  setContactInfo: React.Dispatch<SetStateAction<ContactProps>>;
  titleLabel: string;
  formSubmitted: boolean;
}) => {
  const { translatedText } = useContext(LocaleContext);
  return (
    <>
      <Grid container size={contactInfo?.contactSubTypeId === 5 ? 6 : 12} id='TITLE-DD'>
        <SelectInput
          id='title-select'
          label={titleLabel}
          value={
            contactSubTypes?.length > 0 && contactInfo?.contactSubTypeId != null
              ? contactInfo.contactSubTypeId.toString()
              : ''
          }
          hasError={formSubmitted && contactInfo.contactSubTypeId == null}
          required
          selectOptions={contactSubTypes.map((choice: ContactSubType) => {
            return {
              label: choice.name,
              value: choice.id,
            };
          })}
          onChange={(e: SelectChangeEvent) => {
            if (e.target.value !== '') {
              const choice = contactSubTypes.find(
                (c: ContactSubType) => c.id === Number(e.target.value)
              );

              if (choice != null) {
                setContactInfo({
                  ...contactInfo,
                  contactSubTypeId: choice.id,
                  title: choice.name,
                });
              }
            }
          }}
        />
      </Grid>

      {contactInfo?.contactSubTypeId === 5 && (
        <Grid container size={6} id='title-other'>
          <TextField
            fullWidth
            onBlur={(e) => {
              setContactInfo({
                ...contactInfo,
                title: e.target.value,
              });
            }}
            defaultValue={contactInfo?.title != null ? contactInfo?.title : ''}
            id='titleOther'
            variant='outlined'
            label={translatedText.signup.labelSubCategoryOther}
            InputProps={{
              startAdornment: <Box />,
            }}
            inputProps={{ maxLength: 50 }}
          />
          {contactInfo.title != null && contactInfo.title.length >= 50 && (
            <CharacterLimitHelperText limit={50} />
          )}
        </Grid>
      )}
    </>
  );
};

export default function ContactModal({
  contactConfig,
  contact,
  onClose,
  onSave,
}: {
  contactConfig: ContactConfig;
  contact: ContactProps;
  onClose: (updatedContact?: ContactProps | null) => void;
  onSave: (contact: ContactProps) => Promise<boolean | undefined>;
}) {
  const { contactTypes, allCountries } = useContext(ConfigContext);
  const { currentApplication, partnerGroupId, partnerInfo } = useContext(CurrentApplicationContext);
  const { translatedText } = useContext(LocaleContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [whatsAppCountryCode, setWhatsAppCountryCode] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const [formSubmitted, setFormSubmitted] = useState<boolean>(() => contact?.id != null);
  const [showWhatsApp, setShowWhatsApp] = useState<boolean>(false);

  const [contactInfo, setContactInfo] = useState<ContactProps>(contact);

  const contactSubTypes =
    contactTypes.find((c: ContactType) => c.id === contactConfig.id)?.contactSubTypes ?? [];

  // default the countryId to the partner's countryId if it exists
  //  and the contact doesn't have a countryId or id,
  // otherwise default to USCountryId
  useEffect(() => {
    if (contactInfo?.countryId != null || contactInfo?.id != null) return;

    setContactInfo((prevContactInfo) => ({
      ...prevContactInfo,
      countryId:
        prevContactInfo?.countryId ??
        (selectedProgramId === ProgramNames.TreeCitiesWorld ? partnerInfo?.countryId : USCountryId),
    }));
  }, [contactInfo?.countryId, contactInfo?.id, partnerInfo?.countryId, selectedProgramId]);

  useEffect(() => {
    if (allCountries.length > 0 || (countryCode != null && whatsAppCountryCode != null)) return;

    const country = allCountries.find((cntry: CountryProps) => cntry.id === contactInfo?.countryId);

    if (countryCode == null) {
      setCountryCode(country?.abbrev ?? 'US');
    }

    if (whatsAppCountryCode == null) {
      setWhatsAppCountryCode(country?.abbrev ?? 'US');
    }
  }, [allCountries, countryCode, contactInfo, whatsAppCountryCode]);

  useEffect(() => {
    if (selectedProgramId === ProgramNames.TreeCitiesWorld && allCountries.length > 0) {
      const country = allCountries.find((x: CountryProps) => contactInfo?.countryId === x.id);
      const code = country?.abbrev;

      setContactInfo((prevContactInfo) => ({
        ...prevContactInfo,
        countryId: prevContactInfo?.countryId,
      }));

      setCountryCode(code ?? null);
      setWhatsAppCountryCode(code ?? null);
    }
  }, [allCountries, contactInfo?.countryId, selectedProgramId]);

  function handleContactChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    event.preventDefault();
    event.stopPropagation();

    const value: string = event.target.value;

    setContactInfo((prevContactInfo) => ({
      ...prevContactInfo,
      [event.target.name]: value,
    }));
  }

  function handleContactAddressChange(address: AddressProps) {
    setContactInfo((prevContactInfo) => ({
      ...prevContactInfo,
      ...address,
    }));
  }

  const handleClose = (updatedContact?: ContactProps | null) => {
    setSaving(false);
    onClose(updatedContact);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormSubmitted(true);
    if (contact.id != null) {
      await handleUpdateContact(contactInfo);
    } else {
      await handleCreateContact(contactInfo);
    }
  };

  const handleCreateContact = async (cntct: ContactProps) => {
    const filledContact = {
      ...cntct,
      phone: cntct?.phone == null ? '' : cntct.phone,
      email: cntct?.email == null ? '' : cntct.email,
      streetAddress1: cntct?.streetAddress1,
      city: cntct?.city,
      countyId: cntct?.countryId !== USCountryId ? '999' : cntct?.countyId,
      stateId: cntct?.countryId !== USCountryId ? '99' : cntct?.stateId,
      countryId: cntct?.countryId,
      zipCode: cntct?.zipCode,
      partnerGroupId: partnerGroupId,
    };

    const success = await onSave(filledContact);
    if (success == true) {
      handleClose(filledContact);
    } else {
      setSaving(false);
    }
  };

  async function handleUpdateContact(updatedContact: ContactProps) {
    const success = await onSave(updatedContact);
    if (success) {
      handleClose(updatedContact);
    } else {
      setSaving(false);
    }
  }

  const error = getContactHasError(contactInfo, contactConfig, selectedProgramId);

  function selectOnFocus(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    e.target.select();
  }

  return (
    <Dialog open={true} onClose={() => handleClose()}>
      <DialogTitleWithClose onClose={() => handleClose()}>
        {contactInfo.id != null
          ? translatedText.contact.editContact
          : translatedText.contact.createContact}
      </DialogTitleWithClose>
      <DialogContent>
        <Grid container rowSpacing={2} columnSpacing={4} gridAutoFlow={'dense'}>
          <Grid display={'flex'} flexDirection='column' size={{ xs: 12, sm: 6 }}>
            <TextField
              id='firstName'
              defaultValue={contactInfo.firstName}
              name='firstName'
              variant='outlined'
              required
              label={translatedText.contact.labelFirstName}
              onFocus={selectOnFocus}
              onChange={handleContactChange}
              error={
                formSubmitted && (contactInfo.firstName === '' || contactInfo.firstName === null)
              }
              inputProps={{ maxLength: 500 }}
            />
            {contactInfo.firstName != null && contactInfo.firstName.length >= 500 && (
              <CharacterLimitHelperText limit={500} />
            )}
          </Grid>
          <Grid display={'flex'} flexDirection='column' size={{ xs: 12, sm: 6 }}>
            <TextField
              id='lastName'
              defaultValue={contactInfo.lastName}
              name='lastName'
              variant='outlined'
              required
              label={translatedText.contact.labelLastNameLabel}
              onFocus={selectOnFocus}
              onChange={handleContactChange}
              error={
                formSubmitted && (contactInfo.lastName === '' || contactInfo.lastName === null)
              }
              inputProps={{ maxLength: 500 }}
            />
            {contactInfo.lastName != null && contactInfo.lastName.length >= 500 && (
              <CharacterLimitHelperText limit={500} />
            )}
          </Grid>
          {contactConfig.displayTitle && ![32, 33].includes(contactConfig.id) && (
            <Title
              contactInfo={contactInfo}
              handleContactChange={handleContactChange}
              selectOnFocus={selectOnFocus}
              titleLabel={translatedText.contact.labelTitle}
              formSubmitted={formSubmitted}
            />
          )}
          {selectedProgramId === ProgramNames.TreeCampusK12 &&
            contactSubTypes != null &&
            contactSubTypes.length > 0 &&
            [32, 33].includes(contactConfig.id) && (
              <TitleDropdown
                contactInfo={contactInfo}
                contactSubTypes={contactSubTypes}
                setContactInfo={setContactInfo}
                titleLabel={translatedText.contact.labelTitle}
                formSubmitted={formSubmitted}
              />
            )}
        </Grid>

        {contactConfig.displayAddress && (
          <Address
            id={'contact-address'}
            address={{
              streetAddress1: contactInfo.streetAddress1,
              streetAddress2: contactInfo.streetAddress2,
              city: contactInfo.city,
              stateId: contactInfo.stateId,
              countyId: contactInfo.countyId,
              zipCode: contactInfo.zipCode,
              countryId: contactInfo.countryId,
            }}
            onChange={handleContactAddressChange}
            showErrors={formSubmitted}
          />
        )}
        <Grid container rowSpacing={2} columnSpacing={4} gridAutoFlow={'dense'}>
          {contactConfig.displayPhone && (
            <Grid size={{ xs: 12 }}>
              <PhoneNumberSelect
                id='phone'
                label={translatedText.contact.labelPhoneNumber}
                phone={contactInfo?.phone ?? ''}
                required={true}
                defaultCountry={countryCode}
                showError={formSubmitted}
                updatePhoneNumber={(value) => {
                  setContactInfo({
                    ...contactInfo,
                    phone: value,
                  });
                }}
              />
            </Grid>
          )}

          {contactConfig.displayWhatsapp && selectedProgramId === ProgramNames.TreeCitiesWorld && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                ...(!showWhatsApp && {
                  marginTop: -1,
                }),
              }}
            >
              <Box>
                {!showWhatsApp ? (
                  <Button onClick={() => setShowWhatsApp(true)} variant='secondary' size='small'>
                    + Add Whats App Phone Number
                  </Button>
                ) : (
                  <div>
                    <form>
                      <PhoneNumberSelect
                        id='whatsApp'
                        label={translatedText.contact.labelWhatsApp}
                        phone={contactInfo.whatsApp ?? ''}
                        required={true}
                        defaultCountry={whatsAppCountryCode}
                        showError={formSubmitted}
                        updatePhoneNumber={(value) => {
                          setContactInfo({
                            ...contactInfo,
                            whatsApp: value,
                          });
                        }}
                      />
                    </form>
                  </div>
                )}
              </Box>
            </Grid>
          )}

          {contactConfig.displayEmail && (
            <Email
              sx={{ flexGrow: 1 }}
              id='email'
              value={contactInfo.email}
              variant='outlined'
              required
              label={translatedText.contact.labelEmail}
              onFocus={selectOnFocus}
              onChange={handleContactChange}
              error={formSubmitted && (contactInfo.email == '' || contactInfo.email == null)}
            />
          )}

          {contactConfig.displayLinkedIn && (
            <Grid display={'flex'} flexDirection={'column'} size={12}>
              <TextField
                id='linkedIn'
                defaultValue={contactInfo.linkedIn}
                name={'linkedIn'}
                label='LinkedIn'
                variant='outlined'
                onChange={handleContactChange}
                onFocus={selectOnFocus}
              />
            </Grid>
          )}

          {contactConfig.displayDepartment && (
            <Department
              setContactInfo={setContactInfo}
              contactInfo={contactInfo}
              label={'Home Department'}
              formSubmitted={formSubmitted}
              contactTypeId={contactConfig.id}
            />
          )}

          {contactConfig.displayCertification && (
            <Certification
              contactInfo={contactInfo}
              handleContactChange={handleContactChange}
              formSubmitted={formSubmitted}
            />
          )}
        </Grid>

        {/* Display a warning message telling the user that updating the contact will change the contact anywhere this contact is also used. */}
        {contactInfo.id != null && (
          <Alert severity='info'>
            {translatedText.contact.editWarning(currentApplication.applicationCycle ?? '')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='primary' size='large' disabled={saving || error} onClick={handleSave}>
          <Box display={'flex'} gap={1} flexDirection={'row'}>
            {saving
              ? translatedText.common.buttonSaving
              : contactInfo?.id != null && contactInfo.id > 0
                ? translatedText.contact.buttonUpdate
                : translatedText.contact.buttonAdd}

            {saving && <Spinner size={18} type={'circular'} />}
          </Box>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
